import _ from 'lodash';
import type { Router, RouteLocationNormalized } from 'vue-router';
import { subscriptionDialogManager } from '@/utils/subscription-dialog-manager';
import { prepareTokenDeferred } from '@eureka/shared/src/utils/account';
import { removeHistoryQueries } from '@/utils/url';

/**
 * 支付成功处理服务
 * 负责处理支付成功后的所有业务逻辑：验证支付、显示成功弹窗、刷新token、清理路由参数
 */
export class PaymentSuccessHandler {
    private router: Router;

    constructor(router: Router) {
        this.router = router;
    }

    /**
     * 处理支付成功回调
     * @param route 当前路由对象
     */
    async handlePaymentCallback(route: RouteLocationNormalized): Promise<void> {
        // 尝试从路由 query 或 URL search 中获取 payment_state
        const urlParams = new URLSearchParams(window.location.search);
        const payment_state = (route.query.payment_state as string) || urlParams.get('payment_state');

        // 只支持 f-eureka 风格 de payment_state 参数
        if (payment_state === 'success') {
            await this.processPaymentSuccess(route);
        } else if (payment_state === 'cancel') {
            // 如果是取消状态，也清理一下 URL
            await this.cleanupUrl(route);
        }
    }

    /**
     * 处理支付成功（通用逻辑）
     * @param route 当前路由对象
     */
    private async processPaymentSuccess(route: RouteLocationNormalized): Promise<void> {
        try {
            // 1. 刷新权限
            await this.refreshUserPermissions();
            // 2. 立即清理URL参数，避免刷新重复触发
            await this.cleanupUrl(route);
            // 3. 显示成功提示（弹窗模式）
            // 注意：showSuccessNotification 会等待弹窗关闭，所以放在最后
            await this.showSuccessNotification();
        } catch (error) {
            await this.handlePaymentError(error as Error);
        }
    }

    /**
     * 刷新用户权限和套餐信息
     */
    private async refreshUserPermissions(): Promise<void> {
        try {
            // 1. 清除本地 token，强制重新获取带新权限的 token
            const passport = await prepareTokenDeferred.promise;
            if (passport) {
                localStorage.removeItem('patsnap_header_account_info');
                passport.removeLocal();
            }

            // 2. 刷新基础权限 (Identity / Startup)
            if (typeof window.__reinitializeApp === 'function') {
                await window.__reinitializeApp();
            } else {
                console.warn('__reinitializeApp function not available, skipping token refresh');
            }

            // 3. 刷新套餐信息 (Pro/Free 状态)
            try {
                const { useUserPackage } = await import('@/composables/useUserPackage');
                const { fetchPackageInfo } = useUserPackage();
                await fetchPackageInfo();
            } catch (pkgError) {
                console.error('Failed to refresh package info:', pkgError);
            }
        } catch (error) {
            console.error('Failed to refresh user permissions:', error);
        }
    }

    /**
     * 显示成功通知（弹窗模式）
     */
    private async showSuccessNotification(): Promise<void> {
        try {
            await subscriptionDialogManager.showSuccessDialog();
        } catch (error) {
            console.error('Failed to show success dialog:', error);
            // 降级到浏览器原生通知
            this.showFallbackNotification();
        }
    }

    /**
     * 降级通知方案
     */
    private showFallbackNotification(): void {
        // 使用浏览器原生通知作为降级方案
        if ('Notification' in window && Notification.permission === 'granted') {
            new Notification('Payment Successful', {
                body: 'Your subscription has been activated successfully.',
                icon: '/favicon.ico',
            });
        } else {
            // 最后降级到 alert
            alert('Payment successful! Your subscription has been activated.');
        }
    }

    /**
     * 清理URL参数
     * @param route 当前路由对象
     */
    private async cleanupUrl(route: RouteLocationNormalized): Promise<void> {
        try {
            // 清理 Vue Router query 中的 payment_state
            const cleanQuery = _.omit(route.query, ['payment_state']);
            await this.router.replace({ path: route.path, query: cleanQuery });

            // 清理 hash 内外的 payment_state（兼容 Stripe 回调两种位置）
            removeHistoryQueries(['payment_state']);
        } catch (error) {
            console.error('Failed to cleanup URL parameters:', error);
        }
    }

    /**
     * 处理支付错误
     * @param error 错误对象
     */
    private async handlePaymentError(error: Error): Promise<void> {
        console.error('Payment processing error:', error);

        try {
            // 显示错误提示
            if (subscriptionDialogManager.showErrorDialog) {
                await subscriptionDialogManager.showErrorDialog(error.message);
            } else {
                alert(`Payment processing failed: ${error.message}`);
            }
        } catch (dialogError) {
            console.error('Failed to show error dialog:', dialogError);
            alert(`Payment processing failed: ${error.message}`);
        }
    }

    /**
     * 检查是否为支付成功回调
     * 兼容 Hash 模式 and History 模式
     */
    static isPaymentSuccessCallback(route: RouteLocationNormalized): boolean {
        // 检查路由 query
        if (route.query?.payment_state === 'success') return true;

        // 检查 URL search (兼容 Hash 模式下参数在 # 之前的情况)
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get('payment_state') === 'success';
    }

    /**
     * 兼容性方法：保持向后兼容
     * @deprecated 使用 handlePaymentCallback 替代
     */
    async handlePaymentSuccess(route: any): Promise<void> {
        return this.handlePaymentCallback(route);
    }
}
